import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { trackButtonClick } from "../analytics/ga";
import { fetchPublicLibrarySnippets } from "../api/snippets";
import { useAuth } from "../auth/AuthContext";
import { CHROME_EXTENSION_URL } from "../constants/links";
import {
  DEFAULT_SUGGESTION_HIGHLIGHT,
  getSuggestionHighlightOption,
  LANGUAGE_OPTIONS,
  normalizeProfileLanguage,
} from "../constants/profileOptions";
import { SUPPORTED_LANGUAGES, t } from "../i18n";
import { useLanguage } from "../i18n/LanguageContext";
import "../components/AppShell.css";
import "./PublicPlaygroundPage.css";
import { trackEvent } from "../analytics/ga";

const resolveSnippetLanguage = (code) =>
  normalizeProfileLanguage(code) || "en";

const scoreSuggestion = (query, text, timesUsed) => {
  const q = query.toLocaleLowerCase();
  const resemblance = q.length / Math.max(text.length, 1);
  const usageBoost = Math.log1p(timesUsed ?? 0) * 0.15;
  return resemblance + usageBoost;
};

const capitalizeFirstLetter = (text, firstLetterCapitalized) => {
  if (!text) return text;
  if (firstLetterCapitalized) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  return text.charAt(0).toLowerCase() + text.slice(1);
};

export const PublicPlaygroundPage = () => {
  const { user } = useAuth();
  const { language, setLanguage, portal } = useLanguage();
  const copy = portal.publicPlayground;
  const common = portal.common;
  const contentLanguages = portal.profile?.contentLanguages || {};
  const textareaRef = useRef(null);
  const mirrorRef = useRef(null);
  const queryRef = useRef("");
  const languageMenuRef = useRef(null);
  const snippetLanguageMenuRef = useRef(null);

  const [note, setNote] = useState("");
  const [snippets, setSnippets] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [caretPosition, setCaretPosition] = useState({ top: 0, left: 0 });
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [snippetLanguageMenuOpen, setSnippetLanguageMenuOpen] = useState(false);
  const [snippetLanguage, setSnippetLanguage] = useState(() =>
    resolveSnippetLanguage(language)
  );

  const highlightStyle = useMemo(
    () => getSuggestionHighlightOption(DEFAULT_SUGGESTION_HIGHLIGHT),
    []
  );

  const currentLanguage =
    SUPPORTED_LANGUAGES.find(({ code }) => code === language) ??
    SUPPORTED_LANGUAGES[0];

  const currentSnippetLanguage =
    LANGUAGE_OPTIONS.find(({ code }) => code === snippetLanguage) ??
    LANGUAGE_OPTIONS.find(({ code }) => code === "en") ??
    LANGUAGE_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setLanguageMenuOpen(false);
      }
      if (
        snippetLanguageMenuRef.current &&
        !snippetLanguageMenuRef.current.contains(event.target)
      ) {
        setSnippetLanguageMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const nextSnippets = await fetchPublicLibrarySnippets(snippetLanguage);
      setSnippets(Array.isArray(nextSnippets) ? nextSnippets : []);
    } catch (err) {
      setError(err?.message || copy.loadFailed);
      setSnippets([]);
    } finally {
      setLoading(false);
    }
  }, [copy.loadFailed, snippetLanguage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSuggestions([]);
    queryRef.current = "";
  }, [snippetLanguage]);

  const updateSuggestions = useCallback(
    (query) => {
      if (query.length <= 1) {
        setSuggestions([]);
        return;
      }

      const firstLetterCapitalized =
        query.charAt(0).toUpperCase() === query.charAt(0);
      const queryLower = query.toLocaleLowerCase();
      const next = snippets
        .filter((snippet) =>
          (snippet.text || "").toLocaleLowerCase().startsWith(queryLower)
        )
        .sort((a, b) => {
          const scoreDiff =
            scoreSuggestion(query, b.text, b.times_used ?? 0) -
            scoreSuggestion(query, a.text, a.times_used ?? 0);
          if (scoreDiff !== 0) return scoreDiff;
          return a.text.length - b.text.length;
        })
        .slice(0, 10)
        .map((snippet) => ({
          _id: snippet._id,
          text: capitalizeFirstLetter(snippet.text, firstLetterCapitalized),
        }));

      setSuggestions(next);
      setHighlightedIndex(0);
    },
    [snippets]
  );

  const getCaretCoordinates = useCallback(() => {
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!textarea || !mirror) return;

    const computed = window.getComputedStyle(textarea);
    const rect = textarea.getBoundingClientRect();

    mirror.style.fontFamily = computed.fontFamily;
    mirror.style.fontSize = computed.fontSize;
    mirror.style.fontWeight = computed.fontWeight;
    mirror.style.fontStyle = computed.fontStyle;
    mirror.style.letterSpacing = computed.letterSpacing;
    mirror.style.textTransform = computed.textTransform;
    mirror.style.padding = computed.padding;
    mirror.style.border = computed.border;
    mirror.style.width = `${textarea.clientWidth}px`;
    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.wordBreak = computed.wordBreak;
    mirror.style.lineHeight = computed.lineHeight;
    mirror.style.boxSizing = computed.boxSizing;
    mirror.style.visibility = "hidden";
    mirror.style.position = "fixed";
    mirror.style.top = `${rect.top}px`;
    mirror.style.left = `${rect.left}px`;
    mirror.style.height = `${textarea.clientHeight}px`;
    mirror.style.overflow = "hidden";
    mirror.style.pointerEvents = "none";
    mirror.style.zIndex = "-1";

    const selectionStart = textarea.selectionStart ?? 0;
    mirror.textContent = (textarea.value || "").substring(0, selectionStart);
    mirror.scrollTop = textarea.scrollTop;
    mirror.scrollLeft = textarea.scrollLeft;

    const marker = document.createElement("span");
    marker.textContent = "\u200b";
    mirror.appendChild(marker);
    const markerRect = marker.getBoundingClientRect();
    mirror.removeChild(marker);

    const lineHeight = Number.parseFloat(computed.lineHeight);
    const fontSize = Number.parseFloat(computed.fontSize);
    const effectiveLineHeight = Number.isFinite(lineHeight)
      ? lineHeight
      : Number.isFinite(fontSize)
        ? fontSize * 1.2
        : 16;

    const wrapRect = textarea.parentElement?.getBoundingClientRect();
    setCaretPosition({
      top: markerRect.top + effectiveLineHeight + 2 - (wrapRect?.top || 0),
      left: markerRect.left - (wrapRect?.left || 0),
    });
  }, []);

  const selectSuggestion = useCallback(
    (suggestion) => {
      const textarea = textareaRef.current;
      if (!textarea || !suggestion) return;

      const currentText = textarea.value;
      const selectionStart = textarea.selectionStart ?? cursor;
      const beforeCursor = currentText.slice(0, selectionStart);
      const afterCursor = currentText.slice(selectionStart);
      const query = queryRef.current;
      const beforeQuery = beforeCursor.slice(
        0,
        beforeCursor.length - query.length
      );
      const nextText = beforeQuery + suggestion.text + afterCursor;
      const nextCaret = beforeQuery.length + suggestion.text.length;

      textarea.value = nextText;
      textarea.selectionStart = nextCaret;
      textarea.selectionEnd = nextCaret;
      setNote(nextText);
      setCursor(nextCaret);
      setSuggestions([]);
      queryRef.current = "";
      requestAnimationFrame(getCaretCoordinates);
    },
    [cursor, getCaretCoordinates]
  );

  const handleInput = (event) => {
    const textarea = event.target;
    const value = textarea.value;
    const selectionStart = textarea.selectionStart ?? value.length;

    setNote(value);
    setCursor(selectionStart);
    getCaretCoordinates();

    const textBeforeCursor = value.slice(0, selectionStart);
    const lastLine = textBeforeCursor.split("\n").pop() || "";
    const words = lastLine.trimEnd().split(/\s+/).filter(Boolean);
    let query = "";
    for (let i = 0; i < words.length; i += 1) {
      const candidate = words.slice(i).join(" ");
      const hasMatch = snippets.some((snippet) =>
        (snippet.text || "")
          .toLocaleLowerCase()
          .startsWith(candidate.toLocaleLowerCase())
      );
      if (hasMatch) {
        query = candidate;
        break;
      }
    }
    if (!query && words.length > 0) query = words[words.length - 1];
    queryRef.current = query;
    updateSuggestions(query);
  };

  const handleKeyDown = (event) => {
    if (suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex(
        (prev) => (prev - 1 + suggestions.length) % suggestions.length
      );
    } else if (event.key === "Tab") {
      event.preventDefault();
      selectSuggestion(suggestions[highlightedIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setSuggestions([]);
    }
  };

  useLayoutEffect(() => {
    getCaretCoordinates();
  }, [note, suggestions, getCaretCoordinates]);

  const handleLanguageSelect = (code) => {
    setLanguage(code);
    setLanguageMenuOpen(false);
  };

  const handleSnippetLanguageSelect = (code) => {
    setSnippetLanguage(resolveSnippetLanguage(code));
    setSnippetLanguageMenuOpen(false);
    trackButtonClick(`playground_snippet_language_${code}`);
  };

  return (
    <div className="app-shell public-playground">
      <header className="app-header">
        <div className="app-header-inner">
          <div className="app-header-top">
            <Link
              to="/"
              className="public-playground-back"
              onClick={() => trackButtonClick("playground_back_to_website")}
            >
              {copy.backToWebsite}
            </Link>
            <Link to="/" className="app-brand">
              FastLex
            </Link>
            <div className="app-header-meta">
              <div
                className="language-dropdown app-language-dropdown"
                ref={languageMenuRef}
              >
                <button
                  type="button"
                  className="language-dropdown-toggle"
                  onClick={() => setLanguageMenuOpen((open) => !open)}
                  aria-expanded={languageMenuOpen}
                  aria-haspopup="listbox"
                  aria-label={common.selectLanguage}
                >
                  <span className="language-dropdown-label">
                    {currentLanguage.label}
                  </span>
                  <span className="language-dropdown-chevron" aria-hidden="true">
                    ▾
                  </span>
                </button>
                {languageMenuOpen && (
                  <ul className="language-dropdown-menu" role="listbox">
                    {SUPPORTED_LANGUAGES.map(({ code, name }) => (
                      <li key={code} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={language === code}
                          className={`language-dropdown-item ${language === code ? "active" : ""
                            }`}
                          onClick={() => handleLanguageSelect(code)}
                        >
                          {name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Link
                to={user ? "/app/snippets" : "/login"}
                className="public-playground-header-link"
                onClick={() =>
                  trackButtonClick(
                    user ? "playground_open_app" : "playground_sign_in"
                  )
                }
              >
                {user ? common.openApp : portal.login.signIn}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="app-intro">
          <h1>{copy.title}</h1>
          <p>{copy.description}</p>
        </div>

        <section className="public-playground-ctas" aria-label={copy.ctaAria}>
          <a
            href={CHROME_EXTENSION_URL}
            className="nav-download-btn public-playground-cta"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent({ category: "Public Playground", action: "Download Extension" })}
          >
            {copy.downloadExtension}
          </a>
        </section>

        {error && <div className="app-alert">{error}</div>}

        <section className="app-form-card">
          <div className="app-toolbar">
            <span className="public-playground-count">
              {loading
                ? common.loading
                : t(copy.snippetCount, { count: snippets.length })}
            </span>
            <div className="app-toolbar-actions">
              <div
                className="language-dropdown public-playground-snippet-language"
                ref={snippetLanguageMenuRef}
              >
                <button
                  type="button"
                  className="language-dropdown-toggle"
                  onClick={() =>
                    setSnippetLanguageMenuOpen((open) => !open)
                  }
                  aria-expanded={snippetLanguageMenuOpen}
                  aria-haspopup="listbox"
                  aria-label={copy.selectSnippetLanguage}
                  disabled={loading}
                >
                  <span className="language-dropdown-label">
                    {contentLanguages[currentSnippetLanguage.code] ||
                      currentSnippetLanguage.label}
                  </span>
                  <span
                    className="language-dropdown-chevron"
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </button>
                {snippetLanguageMenuOpen && (
                  <ul
                    className="language-dropdown-menu public-playground-snippet-language-menu"
                    role="listbox"
                  >
                    {LANGUAGE_OPTIONS.map(({ code, label }) => (
                      <li key={code} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={snippetLanguage === code}
                          className={`language-dropdown-item ${
                            snippetLanguage === code ? "active" : ""
                          }`}
                          onClick={() => handleSnippetLanguageSelect(code)}
                        >
                          {contentLanguages[code] || label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="try-note-wrap">
            <textarea
              ref={textareaRef}
              className="try-note"
              value={note}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              onClick={getCaretCoordinates}
              onKeyUp={getCaretCoordinates}
              onScroll={getCaretCoordinates}
              placeholder={copy.placeholder}
              disabled={loading}
              aria-label={copy.ariaLabel}
            />
            <div ref={mirrorRef} aria-hidden="true" />
            {suggestions.length > 0 && (
              <div
                className="try-suggestions"
                style={{
                  top: caretPosition.top,
                  left: caretPosition.left,
                }}
                role="listbox"
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion._id}
                    type="button"
                    className={
                      index === highlightedIndex
                        ? "try-suggestion highlighted"
                        : "try-suggestion"
                    }
                    style={
                      index === highlightedIndex
                        ? {
                          backgroundColor: highlightStyle.bgcolor,
                          outline: `1px solid ${highlightStyle.borderColor}`,
                          outlineOffset: "-1px",
                        }
                        : undefined
                    }
                    onMouseDown={(event) => {
                      event.preventDefault();
                      selectSuggestion(suggestion);
                    }}
                    role="option"
                    aria-selected={index === highlightedIndex}
                  >
                    <span>{suggestion.text}</span>
                    {index === highlightedIndex && (
                      <span className="try-tab-hint">{copy.tabHint}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <ul className="try-instructions" style={{ marginTop: 16 }}>
            <li>
              {copy.instruction1Before}{" "}
              <strong>{copy.instruction1Tab}</strong> {copy.instruction1End}
            </li>
            <li>
              {copy.instruction3Before}{" "}
              <Link
                to="/login"
                onClick={() => trackButtonClick("playground_sign_up_link")}
              >
                {copy.signUpLink}
              </Link>
              {copy.instruction3End}
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
};
